import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ShieldCheck, ArrowRight, SignOut } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import Brand from "@/components/Brand";

/**
 * Forced / voluntary password change page.
 *
 * • When the `force_password_reset` flag is true on `/auth/me`, RequireAuth routes the
 *   user here and locks them out of the rest of `/app/*` until they set a new password.
 * • A signed-in user can also visit `/change-password` voluntarily from Settings.
 */
export default function ChangePassword() {
  const { user, refreshMe, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const forced = !!user?.force_password_reset;

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Inline client-side strength checks — mirrors backend rules in /auth/change-password
  const newPwTooShort = newPw.length > 0 && newPw.length < 8;
  const mismatch = confirmPw.length > 0 && newPw !== confirmPw;
  const sameAsCurrent = newPw.length > 0 && newPw === currentPw;
  const canSubmit =
    currentPw.length > 0 &&
    newPw.length >= 8 &&
    newPw === confirmPw &&
    newPw !== currentPw &&
    !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      await refreshMe();
      toast.success("Password updated — welcome back");
      // Respect ?next= when set, otherwise go to the app home
      const params = new URLSearchParams(location.search);
      const next = params.get("next") || "/app";
      navigate(next, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Unable to change password";
      toast.error(typeof msg === "string" ? msg : "Unable to change password");
    } finally {
      setSubmitting(false);
    }
  };

  const signOut = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-[var(--bg)]" data-testid="change-password-page">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="w-full max-w-md"
      >
        <div className="flex items-center justify-between mb-6">
          <Brand size="md" />
          <button
            type="button"
            onClick={signOut}
            className="text-xs text-[var(--text-3)] hover:text-[var(--text-1)] inline-flex items-center gap-1"
            data-testid="change-password-signout-btn"
          >
            <SignOut size={14} /> Sign out
          </button>
        </div>

        <div className="glass-card border border-[var(--border)] rounded-2xl p-6 sm:p-8">
          <div className="flex items-start gap-3 mb-5">
            <div className="shrink-0 w-10 h-10 rounded-xl bg-[var(--accent)]/15 text-[var(--accent)] flex items-center justify-center">
              <ShieldCheck size={22} weight="duotone" />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight">
                {forced ? "Set a new password" : "Change password"}
              </h1>
              <p className="text-sm text-[var(--text-3)] mt-1">
                {forced
                  ? "Your tenant admin created this account with a temporary password. Choose a new one to continue."
                  : "Update the password you use to sign in to Clariva."}
              </p>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="current-pw">Current password</Label>
              <div className="relative mt-1">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
                <Input
                  id="current-pw"
                  type="password"
                  autoComplete="current-password"
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  className="pl-9"
                  required
                  data-testid="change-password-current-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="new-pw">New password</Label>
              <div className="relative mt-1">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
                <Input
                  id="new-pw"
                  type="password"
                  autoComplete="new-password"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  className="pl-9"
                  required
                  data-testid="change-password-new-input"
                />
              </div>
              <div className="mt-1 text-xs text-[var(--text-3)]">At least 8 characters</div>
              {newPwTooShort && (
                <div className="mt-1 text-xs text-red-500" data-testid="change-password-err-short">
                  New password must be at least 8 characters
                </div>
              )}
              {sameAsCurrent && (
                <div className="mt-1 text-xs text-red-500" data-testid="change-password-err-same">
                  New password must differ from current
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="confirm-pw">Confirm new password</Label>
              <div className="relative mt-1">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
                <Input
                  id="confirm-pw"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  className="pl-9"
                  required
                  data-testid="change-password-confirm-input"
                />
              </div>
              {mismatch && (
                <div className="mt-1 text-xs text-red-500" data-testid="change-password-err-mismatch">
                  Passwords do not match
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={!canSubmit}
              className="w-full"
              data-testid="change-password-submit-btn"
            >
              {submitting ? "Updating…" : "Update password"} <ArrowRight size={14} className="ms-1" />
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
